/**
 * Admin Gate Component
 * 
 * Displays a notice before entering the admin area.
 * Ensures admins understand they are accessing a restricted area.
 */

import { useState, useEffect } from "react";
import { Shield, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface AdminGateProps {
  children: React.ReactNode;
}

const ADMIN_GATE_KEY = "tj_admin_gate_passed";

export function AdminGate({ children }: AdminGateProps) {
  const [hasEntered, setHasEntered] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check if user has already passed the gate in this session
    const passed = sessionStorage.getItem(ADMIN_GATE_KEY);
    if (passed === "true") {
      setHasEntered(true);
    }
    setIsChecking(false);
  }, []);

  const handleEnter = () => {
    sessionStorage.setItem(ADMIN_GATE_KEY, "true");
    setHasEntered(true);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (hasEntered) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-xl">Admin Access</CardTitle>
          <CardDescription>
            Restricted Area
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Internal Use Only
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  You are accessing the internal Trusted James Admin area. 
                  This dashboard is for operational management and is not visible to agents.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>
              By entering, you confirm you are authorized to access 
              administrative controls and system data.
            </p>
          </div>

          <Button onClick={handleEnter} className="w-full" size="lg">
            Enter Admin
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminGate;
